import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CurrentUser } from '@models/Core/CurrentUser';
import { OpenFeature } from '@openfeature/angular-sdk';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';

import { BusService } from '../bus.service';
import { ConfigService } from '../config.service';
import { EventsService } from '../events.service';
import { RequestService } from '../request.service';
// import {GoogleAnalyticsEventsService} from '../tracking/google-analytics-events.service';
import { GroupChatSocketService } from '../sockets/groupchat.socket.service';
import { IdeaSocketService } from '../sockets/idea.socket.service';
import { SessionSocketService } from '../sockets/session.socket.service';
import { SessionsSocketService } from '../sockets/sessions.socket.service';
import { UserSocketService } from '../sockets/user.socket.service';
import {
  ICreateUserResponse,
  IGetCurrentUserResponse,
  ILoginResponse,
} from './authentication.request.service';

@Injectable()
export class AuthenticationService {
  private currentUser: CurrentUser | any = {};
  private authToken: string;

  userRoles: string[] = [];

  constructor(
    private client: RequestService,
    private bus: BusService,
    private events: EventsService,
    private cookieService: CookieService,
    private router: Router,
    private appConfig: ConfigService,
    private sessionSocket: SessionSocketService,
    private sessionsSocket: SessionsSocketService,
    private ideaSocket: IdeaSocketService,
    private userSocket: UserSocketService,
    private groupChatSocket: GroupChatSocketService,
    // private gaService: GoogleAnalyticsEventsService
  ) {
    this.subscribe();
    this.userRoles = appConfig.userRoles;
  }

  get currentUserSync() {
    if (this.currentUser) {
      return this.currentUser;
    }

    return undefined;
  }

  get getCurrentUser(): Observable<IGetCurrentUserResponse> {
    if (this.currentUser && Object.keys(this.currentUser).length > 0) {
      return of(this.currentUser);
    } else {
      // This is as ugly as it gets. It's only here to maintain expected behaviour.
      return this.client.get<IGetCurrentUserResponse>({
        uri: '/users/me',
        handlers: {
          success: response =>
            this.bus.publish(
              this.events.received.data.user.me.success,
              response,
            ),
          error: error =>
            this.bus.publish(
              this.events.received.data.user.me.failure,
              error.error,
            ),
        },
      });
    }
  }

  get isLoggedIn() {
    if (this.token) {
      return true;
    }

    return false;
  }

  get isAdmin() {
    return this.hasRole('admin');
  }

  canPostInGeneral() {
    return this.hasRole('client-admin');
  }

  get token() {
    if (this.authToken) {
      return this.authToken;
    } else if (this.cookieService.check('token')) {
      return this.cookieService.get('token');
    }

    return undefined;
  }

  /**
   * Connects to all existing sockets with the current user.
   */
  connectSockets() {
    const token = this.token;
    if (token) {
      this.sessionSocket.connect(token);
      this.sessionsSocket.connect(token);
      this.ideaSocket.connect(token);
      this.userSocket.connect(token);
      this.groupChatSocket.connect(token);
    } else {
      console.error('Tried to connect to sockets without token existing.');
    }
  }

  /**
   * Disconnects all existing sockets.
   */
  disconnectSockets() {
    this.sessionSocket.disconnect();
    this.sessionsSocket.disconnect();
    this.ideaSocket.disconnect();
    this.userSocket.disconnect();
    this.groupChatSocket.disconnect();
  }

  /**
   * Checks if the current user has the specified role or higher
   * @param roleName The role to check against
   */
  hasRole(roleName: string) {
    const hasRole = (r, h) => {
      return this.userRoles.indexOf(r) >= this.userRoles.indexOf(h);
    };

    if (arguments.length < 2) {
      return hasRole(this.currentUser.role, roleName);
    }

    const has = Object.prototype.hasOwnProperty.call(this.currentUser, 'role')
      ? hasRole(this.currentUser.role, roleName)
      : false;
    return has;
  }

  private receiveUserAuthAction(body: ILoginResponse | ICreateUserResponse) {
    this.authToken = body.token;
    this.cookieService.set('token', this.authToken);
    this.bus.publish(this.events.requested.data.user.me);
  }

  private receivedCurrentUser(user: IGetCurrentUserResponse) {
    this.currentUser = user;
  }

  private currentUserFailure() {
    this.bus.publish(this.events.notified.user.logout);
  }

  private async logout() {
    this.cookieService.delete('token');
    this.cookieService.delete('connect.sid');
    this.cookieService.delete('_gid');
    this.currentUser = {};
    this.authToken = undefined;
    this.disconnectSockets();

    await OpenFeature.clearContext();

    // this.gaService.emitEvent('nav', 'logout');

    this.router.navigate(['/login']);
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.received.action.authentication.login.success,
      this.receiveUserAuthAction.bind(this),
    );
    this.bus.subscribe(
      this.events.received.data.user.me.success,
      this.receivedCurrentUser.bind(this),
    );
    this.bus.subscribe(
      this.events.received.data.user.me.failure,
      this.currentUserFailure.bind(this),
    );
    this.bus.subscribe(
      this.events.notified.user.logout,
      this.logout.bind(this),
    );
  }
}
